// sessionStorage = data is cleared when the page session ends.
// localStorage = data is bounded to the site, it does not expires
import * as DateFnsNs from 'date-fns'
import { jwtDecode } from 'jwt-decode'
import { Logger, NOOP_VOID } from 'wdc-cube'

export type AccessTokenAboutToExpire = (sender: AppStorage, token: string) => void

type VoidListenerType = () => void

type Authorization = {
    token: string
    rdStation?: object
    theMap?: object
    theActing?: { print: boolean }
    theConsumerMarket?: {
        ia: boolean
        companies: boolean
        partners: boolean
        doctors: boolean
        relationships: boolean
        enchenteRs2024: boolean
        filtroCnpjModoClonagem: boolean
    }
    theConnection?: {
        companies: boolean
        partners: boolean
        doctors: boolean
        relationships: boolean
    }
    userManager?: {
        gestao?: boolean
        auditoria?: boolean
        faturamento?: boolean
    }
    mercadoVet?: object
    analiseDesempenho?: object
    motorCredito?: {
        admin: boolean
        pj: boolean
        pf: boolean
        serasa: boolean
        serasaHistory: boolean
        serpro: boolean
        grupo: boolean
        analise: boolean
        bordero: boolean
        monitoramento: boolean
    }
    position?: string
}

const LOG = Logger.get('AppStorage')

type StorageProperty<T> = {
    get: () => T
    set: (value: unknown, rememberMe?: boolean) => void
    clear: () => void
}

const EXPIRATION_DATE_IN_MILLIS = Date.now()

export class AppStorage {
    private static readonly INSTANCE = new AppStorage()

    public static singleton = () => AppStorage.INSTANCE

    private __eventIdGen = 0

    private __tokenAbouteToExpireMap = new Map<number, AccessTokenAboutToExpire>()
    private __tokenAboutToExpireTimeHandler: VoidListenerType = NOOP_VOID

    private __rememberMeProp = newLocalStorageBoolProperty('3ae9b5f1')
    private __accesssTokenProp = newStorageStringProperty('a9c07324')
    private __rdStationEnabledProp = newStorageBoolProperty('64b990d0')
    private __theMapEnabledProp = newStorageBoolProperty('c9983f7f')
    private __theActingEnabledProp = newStorageBoolProperty('a719206b')
    private __theActingPrintEnabledProp = newStorageBoolProperty('02ea71c8')
    private __theConsumerMarkedEnabledProp = newStorageBoolProperty('42506e97')
    private __theConsumerMarkedIAEnabledProp = newStorageBoolProperty('42506e97-ia')
    private __tcmCompaniesEnabledProp = newStorageBoolProperty('42506e97-cmp')
    private __tcmCompaniesVisibleColumnsProp = newStorageStringProperty('42506e9v7-cmp')
    private __tcmPartnersEnabledProp = newStorageBoolProperty('42506e97-par')
    private __tcmDoctorsEnabledProp = newStorageBoolProperty('42506e97-doc')
    private __tcmRelationshipsEnabledProp = newStorageBoolProperty('42506e97-rel')
    private __tcmEnchenteRs2024EnabledProp = newStorageBoolProperty('42506e97-ersvq')
    private __tcmFilterCnpjModoClonagemEnabledProp = newStorageBoolProperty('42506e97-tfcmc')
    private __tcCompaniesEnabledProp = newStorageBoolProperty('74ea9baa-cmp')
    private __tcCompaniesVisibleColumnsProp = newStorageStringProperty('74ea9bav7-cmp')
    private __tcPartnersEnabledProp = newStorageBoolProperty('74ea9baa-par')
    private __tcDoctorsEnabledProp = newStorageBoolProperty('74ea9baa-doc')
    private __tcRelationshipsEnabledProp = newStorageBoolProperty('74ea9baa-rel')
    private __theConnectionEnabledProp = newStorageBoolProperty('74ea9baa')
    private __userManagerEnabledProp = newStorageBoolProperty('9834a9ax')
    private __adminGestaoEnabledProp = newStorageBoolProperty('9834a9ax-ges')
    private __accessAuditEnabledProp = newStorageBoolProperty('9834a9ax-aud')
    private __billingEnabledProp = newStorageBoolProperty('9834a9ax-fat')
    private __mercadoVetEnabledProp = newStorageBoolProperty('7d3e51c2')
    private __analiseDesempenhoEnabledProp = newStorageBoolProperty('3a9d6e1f')
    private __mcEnabledProp = newStorageBoolProperty('5c1f30aa')
    private __mcAdminEnabledProp = newStorageBoolProperty('5c1f30aa-adm')
    private __mcPjEnabledProp = newStorageBoolProperty('5c1f30aa-pj')
    private __mcPfEnabledProp = newStorageBoolProperty('5c1f30aa-pf')
    private __mcSerasaEnabledProp = newStorageBoolProperty('5c1f30aa-srs')
    private __mcSerasaHistoryEnabledProp = newStorageBoolProperty('5c1f30aa-srsh')
    private __mcSerproEnabledProp = newStorageBoolProperty('5c1f30aa-srp')
    private __mcGrupoEnabledProp = newStorageBoolProperty('5c1f30aa-grp')
    private __mcAnaliseEnabledProp = newStorageBoolProperty('5c1f30aa-anl')
    private __mcBorderoEnabledProp = newStorageBoolProperty('5c1f30aa-bdr')
    private __mcMonitoramentoEnabledProp = newStorageBoolProperty('5c1f30aa-mon')
    private __userNameProp = newStorageStringProperty('J9VCpx')
    private __userPositionProp = newStorageStringProperty('J9CPpxC')

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private constructor() {}

    get rememberMe() {
        return this.__rememberMeProp.get()
    }

    set rememberMe(value: boolean) {
        this.__rememberMeProp.set(value)
    }

    get rdStationEnbled() {
        return this.__rdStationEnabledProp.get()
    }

    get theMapEnabled() {
        return this.__theMapEnabledProp.get()
    }

    get theActingEnabled() {
        return this.__theActingEnabledProp.get()
    }

    get theConsumerMarketEnabled() {
        return this.__theConsumerMarkedEnabledProp.get()
    }

    get theConsumerMarketIAEnabled() {
        return this.__theConsumerMarkedIAEnabledProp.get()
    }

    get tcmCompaniesEnabled() {
        return this.__tcmCompaniesEnabledProp.get()
    }

    get tcmCompaniesVisibleColumns() {
        return this.__tcmCompaniesVisibleColumnsProp.get()
    }

    set tcmCompaniesVisibleColumns(value: string) {
        this.__tcmCompaniesVisibleColumnsProp.set(value, true)
    }

    get tcmPartnersEnabled() {
        return this.__tcmPartnersEnabledProp.get()
    }

    get tcmDoctorsEnabled() {
        return this.__tcmDoctorsEnabledProp.get()
    }

    get tcmRelationshipsEnabled() {
        return this.__tcmRelationshipsEnabledProp.get()
    }

    get tcmEnchenteRs2024Enabled() {
        return this.__tcmEnchenteRs2024EnabledProp.get()
    }

    get tcmFilterCnpjModoClonagemEnabled() {
        return this.__tcmFilterCnpjModoClonagemEnabledProp.get()
    }

    get tcCompaniesEnabled() {
        return this.__tcCompaniesEnabledProp.get()
    }

    get tcCompaniesVisibleColumns() {
        return this.__tcCompaniesVisibleColumnsProp.get()
    }

    set tcCompaniesVisibleColumns(value: string) {
        this.__tcCompaniesVisibleColumnsProp.set(value, true)
    }

    get tcPartnersEnabled() {
        return this.__tcPartnersEnabledProp.get()
    }

    get tcDoctorsEnabled() {
        return this.__tcDoctorsEnabledProp.get()
    }

    get tcRelationshipsEnabled() {
        return this.__tcRelationshipsEnabledProp.get()
    }

    get theActingPrintEnabled() {
        return this.__theActingPrintEnabledProp.get()
    }

    get theConnectionEnabled() {
        return this.__theConnectionEnabledProp.get()
    }

    get userManagerEnabled() {
        return this.__userManagerEnabledProp.get()
    }

    get adminGestaoEnabled() {
        return this.__adminGestaoEnabledProp.get()
    }

    get accessAuditEnabled() {
        return this.__accessAuditEnabledProp.get()
    }

    get billingEnabled() {
        return this.__billingEnabledProp.get()
    }

    get mercadoVetEnabled() {
        return this.__mercadoVetEnabledProp.get()
    }

    get analiseDesempenhoEnabled() {
        return this.__analiseDesempenhoEnabledProp.get()
    }

    get motorCreditoEnabled() {
        return this.__mcEnabledProp.get()
    }

    get motorCreditoAdminEnabled() {
        return this.__mcAdminEnabledProp.get()
    }

    get mcPjEnabled() {
        return this.__mcPjEnabledProp.get()
    }

    get mcPfEnabled() {
        return this.__mcPfEnabledProp.get()
    }

    get mcSerasaEnabled() {
        return this.__mcSerasaEnabledProp.get()
    }

    get mcSerasaHistoryEnabled() {
        return this.__mcSerasaHistoryEnabledProp.get()
    }

    get mcSerproEnabled() {
        return this.__mcSerproEnabledProp.get()
    }

    get mcGrupoEnabled() {
        return this.__mcGrupoEnabledProp.get()
    }

    get mcAnaliseEnabled() {
        return this.__mcAnaliseEnabledProp.get()
    }

    get mcBorderoEnabled() {
        return this.__mcBorderoEnabledProp.get()
    }

    get mcMonitoramentoEnabled() {
        return this.__mcMonitoramentoEnabledProp.get()
    }

    get userPosition(): string {
        return this.__userPositionProp.get()
    }

    get accessTokenExpiration() {
        try {
            const accessToken = this.accessToken
            if (!accessToken) {
                return EXPIRATION_DATE_IN_MILLIS
            }

            const info = decodeAccessToken(accessToken)

            return info.exp * 1000
        } catch (err) {
            LOG.error('[accessTokenExpirationDate]', err)
            return EXPIRATION_DATE_IN_MILLIS
        }
    }

    get accessToken() {
        return this.__accesssTokenProp.get()
    }

    clearAccessToken() {
        this.__accesssTokenProp.clear()
        this.__rdStationEnabledProp.clear()
        this.__theMapEnabledProp.clear()
        this.__theActingEnabledProp.clear()
        this.__theActingPrintEnabledProp.clear()
        this.__theConsumerMarkedEnabledProp.clear()
        this.__theConsumerMarkedIAEnabledProp.clear()
        this.__tcmCompaniesEnabledProp.clear()
        this.__tcmPartnersEnabledProp.clear()
        this.__tcmDoctorsEnabledProp.clear()
        this.__tcmRelationshipsEnabledProp.clear()
        this.__tcmEnchenteRs2024EnabledProp.clear()
        this.__tcmFilterCnpjModoClonagemEnabledProp.clear()
        this.__tcCompaniesEnabledProp.clear()
        this.__tcPartnersEnabledProp.clear()
        this.__tcDoctorsEnabledProp.clear()
        this.__tcRelationshipsEnabledProp.clear()
        this.__theConnectionEnabledProp.clear()
        this.__userManagerEnabledProp.clear()
        this.__adminGestaoEnabledProp.clear()
        this.__accessAuditEnabledProp.clear()
        this.__billingEnabledProp.clear()
        this.__mercadoVetEnabledProp.clear()
        this.__analiseDesempenhoEnabledProp.clear()
        this.__mcEnabledProp.clear()
        this.__mcAdminEnabledProp.clear()
        this.__mcPjEnabledProp.clear()
        this.__mcPfEnabledProp.clear()
        this.__mcSerasaEnabledProp.clear()
        this.__mcSerasaHistoryEnabledProp.clear()
        this.__mcSerproEnabledProp.clear()
        this.__mcGrupoEnabledProp.clear()
        this.__mcAnaliseEnabledProp.clear()
        this.__mcBorderoEnabledProp.clear()
        this.__mcMonitoramentoEnabledProp.clear()
        this.__userPositionProp.clear()

        this.__tokenAboutToExpireTimeHandler()
        this.__tokenAboutToExpireTimeHandler = NOOP_VOID
    }

    get userName(): string {
        return this.__userNameProp.get()
    }

    setUserName(username: string, rememberMe: boolean) {
        this.__userNameProp.set(username, rememberMe)
    }

    setAuthorization(value: Omit<Authorization, 'token'>, rememberMe: boolean) {
        this.__rememberMeProp.set(rememberMe, true)
        this.__rdStationEnabledProp.set(value.rdStation, rememberMe)
        this.__theMapEnabledProp.set(value.theMap, rememberMe)
        this.__theActingEnabledProp.set(value.theActing, rememberMe)
        this.__theActingPrintEnabledProp.set(value.theActing?.print, rememberMe)
        this.__theConsumerMarkedEnabledProp.set(value.theConsumerMarket, rememberMe)
        this.__theConsumerMarkedIAEnabledProp.set(value.theConsumerMarket?.ia, rememberMe)
        this.__tcmCompaniesEnabledProp.set(value.theConsumerMarket?.companies, rememberMe)
        this.__tcmPartnersEnabledProp.set(value.theConsumerMarket?.partners, rememberMe)
        this.__tcmDoctorsEnabledProp.set(value.theConsumerMarket?.doctors, rememberMe)
        this.__tcmRelationshipsEnabledProp.set(value.theConsumerMarket?.relationships, rememberMe)
        this.__tcmEnchenteRs2024EnabledProp.set(value.theConsumerMarket?.enchenteRs2024, rememberMe)
        this.__tcmFilterCnpjModoClonagemEnabledProp.set(value.theConsumerMarket?.filtroCnpjModoClonagem, rememberMe)
        this.__tcCompaniesEnabledProp.set(value.theConnection?.companies, rememberMe)
        this.__tcPartnersEnabledProp.set(value.theConnection?.partners, rememberMe)
        this.__tcDoctorsEnabledProp.set(value.theConnection?.doctors, rememberMe)
        this.__tcRelationshipsEnabledProp.set(value.theConnection?.relationships, rememberMe)
        this.__theConnectionEnabledProp.set(value.theConnection, rememberMe)
        this.__userManagerEnabledProp.set(value.userManager, rememberMe)
        this.__adminGestaoEnabledProp.set(value.userManager?.gestao, rememberMe)
        this.__accessAuditEnabledProp.set(value.userManager?.auditoria, rememberMe)
        this.__billingEnabledProp.set(value.userManager?.faturamento, rememberMe)
        this.__mercadoVetEnabledProp.set(value.mercadoVet, rememberMe)
        this.__analiseDesempenhoEnabledProp.set(value.analiseDesempenho, rememberMe)
        this.__mcEnabledProp.set(value.motorCredito, rememberMe)
        this.__mcAdminEnabledProp.set(value.motorCredito?.admin, rememberMe)
        this.__mcPjEnabledProp.set(value.motorCredito?.pj, rememberMe)
        this.__mcPfEnabledProp.set(value.motorCredito?.pf, rememberMe)
        this.__mcSerasaEnabledProp.set(value.motorCredito?.serasa, rememberMe)
        this.__mcSerasaHistoryEnabledProp.set(value.motorCredito?.serasaHistory, rememberMe)
        this.__mcSerproEnabledProp.set(value.motorCredito?.serpro, rememberMe)
        this.__mcGrupoEnabledProp.set(value.motorCredito?.grupo, rememberMe)
        this.__mcAnaliseEnabledProp.set(value.motorCredito?.analise, rememberMe)
        this.__mcBorderoEnabledProp.set(value.motorCredito?.bordero, rememberMe)
        this.__mcMonitoramentoEnabledProp.set(value.motorCredito?.monitoramento, rememberMe)
        this.__userPositionProp.set(value.position, rememberMe)

        const token = this.__accesssTokenProp.get()
        this.launchTokenExpirationTimeout(token)
    }

    setAccessToken(value: Authorization, rememberMe: boolean) {
        this.clearAccessToken()
        if (!value || !value.token) {
            return
        }
        this.__accesssTokenProp.set(value.token, rememberMe)
        this.setAuthorization(value, rememberMe)
    }

    onAccessTokenAboutToExpire(listener: AccessTokenAboutToExpire) {
        const eventId = this.__eventIdGen++
        this.__tokenAbouteToExpireMap.set(eventId, listener)

        const accessToken = this.accessToken
        if (accessToken) {
            this.launchTokenExpirationTimeout(accessToken)
        } else {
            this.__tokenAboutToExpireTimeHandler()
            this.__tokenAboutToExpireTimeHandler = NOOP_VOID
        }

        return () => {
            this.__tokenAbouteToExpireMap.delete(eventId)
        }
    }

    private launchTokenExpirationTimeout(accessToken: string) {
        this.__tokenAboutToExpireTimeHandler()
        this.__tokenAboutToExpireTimeHandler = NOOP_VOID

        const info = decodeAccessToken(accessToken)
        const millisToExpire = DateFnsNs.differenceInMilliseconds(info.exp * 1000, Date.now())
        if (millisToExpire > 0) {
            const refreshToken = () => {
                for (const listener of this.__tokenAbouteToExpireMap.values()) {
                    try {
                        listener(this, accessToken)
                    } catch (err) {
                        LOG.error(`Processing listner ${listener}`, err)
                    }
                }
            }

            const oneHourInMillis = DateFnsNs.minutesToMilliseconds(60)

            if (millisToExpire < oneHourInMillis) {
                // Do it now
                refreshToken()
            }

            // And, refresh at fix rate of one hour
            this.__tokenAboutToExpireTimeHandler = scheduleAtFixRate(refreshToken, oneHourInMillis)
        }
    }
}

function decodeAccessToken(accessToken: string) {
    return jwtDecode(accessToken) as {
        username: string
        sub: number
        iat: number
        exp: number
    }
}

function scheduleAtFixRate(listner: VoidListenerType, interval: number) {
    const ctx = {
        handler: undefined as NodeJS.Timeout | undefined,
        stoped: false
    }

    const relauchListener: VoidListenerType = () => {
        if (ctx.stoped) {
            return
        }

        try {
            ctx.handler = undefined
            listner()
        } catch (err) {
            LOG.error('[scheduleAtFixRate]', err)
        } finally {
            ctx.handler = setTimeout(relauchListener, interval)
        }
    }

    ctx.handler = setTimeout(relauchListener, interval)

    return () => {
        ctx.stoped = true
        if (ctx.handler) {
            clearTimeout(ctx.handler)
            ctx.handler = undefined
        }
    }
}

function newLocalStorageBoolProperty(uid: string): StorageProperty<boolean> {
    return {
        set: (value: unknown) => {
            localStorage.setItem(uid, value ? 'true' : 'false')
        },
        get: () => {
            return localStorage.getItem(uid) === 'true'
        },
        clear: () => {
            localStorage.removeItem(uid)
        }
    }
}

function newStorageBoolProperty(uid: string): StorageProperty<boolean> {
    return {
        set: (value: unknown, rememberMe?: boolean) => {
            if (rememberMe) {
                localStorage.setItem(uid, value ? 'true' : 'false')
                sessionStorage.removeItem(uid)
            } else {
                sessionStorage.setItem(uid, value ? 'true' : 'false')
                localStorage.removeItem(uid)
            }
        },
        get: () => {
            return localStorage.getItem(uid) === 'true' || sessionStorage.getItem(uid) === 'true'
        },
        clear: () => {
            localStorage.removeItem(uid)
            sessionStorage.removeItem(uid)
        }
    }
}

function newStorageStringProperty(uid: string): StorageProperty<string> {
    return {
        set: (value: unknown, rememberMe?: boolean) => {
            if (rememberMe) {
                if (value) {
                    localStorage.setItem(uid, `${value}`)
                } else {
                    localStorage.removeItem(uid)
                }

                sessionStorage.removeItem(uid)
            } else {
                if (value) {
                    sessionStorage.setItem(uid, `${value}`)
                } else {
                    sessionStorage.removeItem(uid)
                }
                localStorage.removeItem(uid)
            }
        },
        get: () => {
            return localStorage.getItem(uid) || sessionStorage.getItem(uid) || ''
        },
        clear: () => {
            localStorage.removeItem(uid)
            sessionStorage.removeItem(uid)
        }
    }
}

export default AppStorage
